import axios from "axios";
import { HTTP_STATUS_CODES } from "../models/enums/core.enums";
import { ROUTES } from "../models/enums/routes";

const baseURL = process.env.REACT_APP_API_BASE_URL;

export class APIServiceProvider {
  constructor() {
    this.setRequiredDetails()
  }

  setRequiredDetails() {
    const accessToken = localStorage.getItem("accessToken");
    axios.defaults.headers.common["Authorization"] = `${accessToken}`;
    axios.defaults.headers.common["Content-Type"] = "application/json";
    axios.interceptors.response.use((response: any) => response, (error: any) => {
      if (401 === error?.response?.data?.responseCode) {
        // handle error: inform user, go to login, etc
        localStorage.clear();
        sessionStorage.clear();
        window.history.replaceState(null, "", ROUTES.LOGIN);
        window.location.href = ROUTES.LOGIN;
      } else {
        return Promise.reject(error);
      }
    })
  }

  async auth(url: string, body: any) {
    try {
      const response = await axios.post(baseURL + url, body);
      return this.verifyResponseData(response.data);
    } catch (error: any) {
      return this.verifyResponseData(error.response.data);
    }
  }

  async get(url: string, params?: any) {
    try {
      this.setRequiredDetails()
      const response = await axios.get(url, { params: params });
      return response.data;
    } catch (error: any) {
      throw new Error(error?.response?.data?.errorMsg);
    }
  }

  async getWithoutBase(url: string, params?: any) {
    try {
      const response = await axios.get(url, { params: params });
      return this.verifyResponseData(response.data);
    } catch (error: any) {
      throw new Error(error?.message);
    }
  }

  async post(url: string, body?: any, queryParams?: any) {
    try {
      if (!(url.includes("login") || (url.includes("forgot")))) {
        this.setRequiredDetails();
      }
      let response
      if (queryParams) {
        response = await axios.post(url, body, { params: queryParams });
      } else {
        response = await axios.post(url, body);
      }
      return response.data;
    } catch (error: any) {
      if (error?.response) {
        return error?.response.data;
      } else {
        throw new Error(error?.message);
      }
    }
  }

  async postMultipart(url: string, body?: any) {
    try {
      const response = await axios.post(baseURL + url, body, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return this.verifyResponseData(response.data);
    } catch (error: any) {
      if (error?.response) {
        return this.verifyResponseData(error?.response.data);
      } else {
        throw new Error(error?.message);
      }
    }
  }

  async postWithoutBase(url: string, body: any) {
    try {
      const response = await axios.post(url, body);
      return this.verifyResponseData(response.data);
    } catch (error: any) {
      if (error?.response) {
        return this.verifyResponseData(error.response.data);
      } else {
        throw new Error(error?.message);
      }
    }
  }

  async put(url: string, body?: any, queryParams?: any) {
    try {
      let response
      if (queryParams) {
        response = await axios.put(url, body, { params: queryParams });
      } else {
        response = await axios.put(url, body);
      }
      return response.data;
    } catch (error: any) {
      if (error?.response) {
        return error.response.data;
      } else {
        throw new Error(error?.message);
      }
    }
  }

  async patch(url: string, body?: any, queryParams?: any) {
    try {
      let response
      if (queryParams) {
        response = await axios.patch(url, body, { params: queryParams });
      } else {
        response = await axios.patch(url, body);
      }
      return response.data;
    } catch (error: any) {
      if (error?.response) {
        return error.response.data;
      } else {
        throw new Error(error?.message);
      }
    }
  }

  async remove(url: string, body?: any) {
    try {
      const response = await axios.delete(url, { data: body });
      return response.data;
    } catch (error: any) {
      if (error?.response) {
        return error.response.data;
      } else {
        throw new Error(error?.message);
      }
    }
  }

  verifyResponseData(response: any) {
    switch (response?.statusCode) {
      case HTTP_STATUS_CODES.SUCCESS:
        return {
          data: response?.data,
          statusCode: response?.statusCode,
          message: response?.message,
        };
      case HTTP_STATUS_CODES.BAD_REQUEST:
        throw new Error(response?.message);
      case HTTP_STATUS_CODES.RESOURCE_NOT_FOUND:
        throw new Error(response?.message);
      case HTTP_STATUS_CODES.INVALID_ACCESS_TOKEN:
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = ROUTES.AUTH + ROUTES.LOGIN;
        return {
          data: [],
          statusCode: response.statusCode,
          message: response.message,
        };
      case HTTP_STATUS_CODES.ACCESS_DENIED:

    }
  }
}

export const apiservice = new APIServiceProvider();
